import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "../../lib/utils"
import { FOCUS_RING } from "../../lib/focus-ring"
import { Z_CLASS } from "../../lib/z-index"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      `${Z_CLASS.DIALOG} fixed inset-0 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0`,
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const preventDismiss = (event) => {
  event.preventDefault();
};

const DialogContent = React.forwardRef(
  (
    {
      className,
      overlayClassName,
      children,
      dismissible = true,
      onPointerDownOutside,
      onInteractOutside,
      onEscapeKeyDown,
      ...props
    },
    ref
  ) => (
    <DialogPortal>
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          `${Z_CLASS.DIALOG} relative fixed inset-x-0 bottom-0 top-auto grid w-full max-w-none translate-x-0 translate-y-0 gap-2.5 border border-b-0 bg-background p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] shadow-lg duration-200 max-h-[90dvh] overflow-y-auto overflow-x-hidden overscroll-contain rounded-t-xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom md:inset-x-auto md:bottom-auto md:left-[50%] md:top-[50%] md:max-w-dialog-mobile md:translate-x-[-50%] md:translate-y-[-50%] md:gap-3 md:rounded-lg md:border md:p-3 md:pb-3 md:data-[state=closed]:slide-out-to-left-1/2 md:data-[state=closed]:slide-out-to-top-[48%] md:data-[state=open]:slide-in-from-left-1/2 md:data-[state=open]:slide-in-from-top-[48%] md:data-[state=closed]:zoom-out-95 md:data-[state=open]:zoom-in-95 md:max-w-modal-md lg:w-[90vw] lg:max-w-[90vw] lg:max-h-[90dvh] lg:gap-ds-md lg:p-ds-md`,
          className
        )}
        onPointerDownOutside={dismissible ? onPointerDownOutside : preventDismiss}
        onInteractOutside={dismissible ? onInteractOutside : preventDismiss}
        onEscapeKeyDown={dismissible ? onEscapeKeyDown : preventDismiss}
        {...props}
      >
        {children}
        {dismissible ? (
          <DialogPrimitive.Close
            className={cn(
              "dialog-close-control absolute right-3 top-3 z-30 flex min-h-[44px] min-w-[44px] max-lg:min-h-[44px] max-lg:min-w-[44px] items-center justify-center rounded-sm opacity-70 transition-opacity hover:opacity-100 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground md:right-2 md:top-2 lg:min-h-8 lg:min-w-8",
              FOCUS_RING
            )}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        ) : null}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
)
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    data-slot="dialog-header"
    className={cn(
      "flex flex-col space-y-1 text-center sm:text-left max-md:px-11",
      className
    )}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("section-title leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-ds-body text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
